/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types',
	'../../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'../../../app/nls/StringResources'
], function(
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes,
	FormatUtils,
	StringResources
) {

	const getUISpec = (props) => {
		const { item } = props;
		let spec = item;
		if (item && item.editor && item.editor.uiControl) {
			spec = item.editor.uiControl;
		}
		return spec;
	};

	/**
	 * @class InputLabel
	 * @description InputLabel property control class. Supports 2 types of input: regular text and numeric input
	 * 
	 * @example Example props usage to render a number input:
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					placeHolderText: 'no value here',
	 *					readOnly: false,
	 *					inputType: 'number', // defaults to 'text'
	 *					decimalPlaces: 2
	 *				}
	 *			},
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 *			value: 23  // required field
	 *		},
	 *		style: {
	 *			width: '140px'
	 *		},
	 *		className: 'myControlClass'
	 * }
	 * 
	 * @example Example props usage to render a text input:
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					placeHolderText: 'no value here',
	 *					readOnly: false
	 *				}
	 *			},
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 *			value: 23 // required field
	 *		},
	 *		style: {
	 *			width: '140px'
	 *		},
	 *		className: 'myControlClass'
	 * }
	 */
	class InputLabel extends React.Component {

		render() {
			const { style, className } = this.props;
			return (
				<div style={style} className={className}>
					{this.renderControl()}
				</div>
			);
		}

		renderControl() {
			const { item } = this.props;

			const { setPropertyValue, getPropertyValue } = item;
			const {
				id,
				label,
				placeHolderText,
				readOnly,
				inputType,
				invalidText = StringResources.get('propInputLabel_number_invalid')
			} = getUISpec(this.props);

			switch(inputType) {
				case 'number':
					return (
						<BA_UI_CARBON.NumberInput
							id={id}
							invalidText={invalidText}
							label={label}
							value={ getPropertyValue() }
							disabled={readOnly}
							onChange={(event) => {
								const value = event.imaginaryTarget.value;
								const processedValue = FormatUtils.format(value, {
									type: 'number'
								});
								setPropertyValue(processedValue);
							}}
						/>
					);
				case 'text':
				default:
					return (
						<BA_UI_CARBON.TextInput
							id={id}
							value={ getPropertyValue() }
							labelText={label}
							placeholder={placeHolderText}
							disabled={readOnly}
							onChange={(event) => {
								setPropertyValue(event.currentTarget.value);
							}}
						/>
					);
			}
		}
	}

	InputLabel.propTypes = {
		item: PropTypes.shape({
			editor: PropTypes.shape({
				uiControl: PropTypes.shape({
					id: PropTypes.string,
					label: PropTypes.string,
					placeHolderText: PropTypes.string,
					readOnly: PropTypes.bool,
					inputType: PropTypes.string,
					invalidText: PropTypes.string,
					decimalPlaces: PropTypes.number
				})
			}),
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			placeHolderText: PropTypes.string,
			readOnly: PropTypes.bool,
			setPropertyValue: PropTypes.func.isRequired,
			getPropertyValue: PropTypes.func.isRequired
		}),
		style: PropTypes.object,
		className: PropTypes.string
	};
	InputLabel.defaultProps = {
		style: {}
	};

	return MobxReact.observer(InputLabel);
});